import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  buildHoldings,
  groupByInstrument,
  isCurrency,
  type Holding,
  type HoldingSource,
  type InstrumentHoldings,
  type InvestmentTransaction,
  type PositionSnapshot,
} from '@budget/core';
import { supabase } from '../../lib/supabase.js';
import { queryKeys } from '../../lib/query-client.js';

/**
 * Every `::text` here is load-bearing: PostgREST renders `numeric` as a JSON
 * number, which would hand Decimal a value that has already lost precision.
 * See apps/web/src/types/db.ts.
 */
const ACCOUNT_SELECT = 'id, provider, name, currency, position_source, positions_as_of';
const TRANSACTION_SELECT =
  'id, account_id, instrument_id, type, quantity::text, price::text, amount::text, currency, trade_date, external_id';
const SNAPSHOT_SELECT =
  'account_id, instrument_id, as_of, quantity::text, cost_basis::text, market_value::text, currency, source';
const INSTRUMENT_SELECT = 'id, ticker, name, isin, currency, asset_class, exchange';

export interface PortfolioAccount {
  id: string;
  provider: string;
  name: string;
  positionSource: HoldingSource;
}

export interface PortfolioInstrument {
  id: string;
  ticker: string;
  name: string;
  isin: string | null;
  assetClass: string;
}

export interface Portfolio {
  holdings: Holding[];
  byInstrument: InstrumentHoldings[];
  accounts: Map<string, PortfolioAccount>;
  instruments: Map<string, PortfolioInstrument>;
}

export function usePortfolio({ enabled }: { enabled: boolean }) {
  const query = useQuery({
    queryKey: queryKeys.portfolio,
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      // One round trip each, in parallel. The whole portfolio is a few hundred
      // rows, so paging or a server-side join would be premature.
      const [accounts, instruments, transactions, snapshots] = await Promise.all([
        supabase.from('accounts').select(ACCOUNT_SELECT).eq('is_active', true),
        supabase.from('instruments').select(INSTRUMENT_SELECT),
        supabase.from('investment_transactions').select(TRANSACTION_SELECT),
        supabase.from('position_snapshots').select(SNAPSHOT_SELECT),
      ]);

      for (const result of [accounts, instruments, transactions, snapshots]) {
        if (result.error) throw result.error;
      }

      return {
        accounts: accounts.data ?? [],
        instruments: instruments.data ?? [],
        transactions: transactions.data ?? [],
        snapshots: snapshots.data ?? [],
      };
    },
  });

  const portfolio = useMemo<Portfolio | null>(() => {
    if (!query.data) return null;

    const accounts = new Map<string, PortfolioAccount>();
    const accountSources = new Map<string, HoldingSource>();
    // Without this an account sold down to nothing keeps its last holdings:
    // it reports no positions, so no rows arrive, and the newest day that has
    // rows is still the day before the sale.
    const snapshotAsOf = new Map<string, string>();
    for (const row of query.data.accounts) {
      accounts.set(row.id, {
        id: row.id,
        provider: row.provider,
        name: row.name,
        positionSource: row.position_source,
      });
      accountSources.set(row.id, row.position_source);
      if (row.positions_as_of !== null) snapshotAsOf.set(row.id, row.positions_as_of);
    }

    const instruments = new Map<string, PortfolioInstrument>();
    for (const row of query.data.instruments) {
      instruments.set(row.id, {
        id: row.id,
        ticker: row.ticker,
        name: row.name,
        isin: row.isin,
        assetClass: row.asset_class,
      });
    }

    const holdings = buildHoldings({
      transactions: query.data.transactions.flatMap(toTransaction),
      snapshots: query.data.snapshots.flatMap(toSnapshot),
      accountSources,
      snapshotAsOf,
    });

    return {
      holdings,
      byInstrument: groupByInstrument(holdings),
      accounts,
      instruments,
    };
  }, [query.data]);

  return { ...query, portfolio };
}

type TransactionRow = {
  id: string;
  account_id: string;
  instrument_id: string | null;
  type: InvestmentTransaction['type'];
  quantity: string | null;
  price: string | null;
  amount: string;
  currency: string;
  trade_date: string;
  external_id: string | null;
};

/** Rows in a currency v1 cannot price are skipped rather than crashing a page. */
function toTransaction(row: TransactionRow): InvestmentTransaction[] {
  if (!isCurrency(row.currency)) return [];
  return [
    {
      id: row.id,
      accountId: row.account_id,
      instrumentId: row.instrument_id,
      type: row.type,
      quantity: row.quantity,
      price: row.price,
      amount: row.amount,
      currency: row.currency,
      tradeDate: row.trade_date,
      externalId: row.external_id,
    },
  ];
}

type SnapshotRow = {
  account_id: string;
  instrument_id: string;
  as_of: string;
  quantity: string;
  cost_basis: string | null;
  market_value: string | null;
  currency: string;
  source: string;
};

function toSnapshot(row: SnapshotRow): PositionSnapshot[] {
  if (!isCurrency(row.currency)) return [];
  return [
    {
      accountId: row.account_id,
      instrumentId: row.instrument_id,
      asOf: row.as_of,
      quantity: row.quantity,
      costBasis: row.cost_basis,
      marketValue: row.market_value,
      currency: row.currency,
      source: row.source,
    },
  ];
}
